import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { createWorkflowDefinition, getAllWorkflowDefinitions } from '../../../services/workflowService';
import { useProtocols } from '../../../hooks/useProtocols';
import { useNotifications } from '../../NotificationSystem';
import './SetupWizardSteps.css';

const Step11Workflows = ({ stepData, onComplete, onValidation, userData, structure }) => {
  const { notify } = useNotifications();
  const { t } = useTranslation('setup');
  const [loading, setLoading] = useState(false);
  const [workflows, setWorkflows] = useState([]);
  const organizationId = structure?.organizations?.[0]?.id || null;
  const { protocols } = useProtocols({});

  useEffect(() => {
    const loadWorkflows = async () => {
      try {
        const allWorkflows = await getAllWorkflowDefinitions();
        setWorkflows(allWorkflows || []);
        if ((allWorkflows || []).length >= 1) {
          onComplete({ workflows: allWorkflows.length, requirementsMet: true });
        }
      } catch (error) {
        console.error('Error loading workflows:', error);
      }
    };
    loadWorkflows();
  }, []);

  const handleCreateBasicWorkflow = async () => {
    if (!organizationId) {
      notify.error(t('step11.notifications.missingOrganization'), t('step11.notifications.missingOrganizationTitle'));
      return;
    }

    setLoading(true);
    try {
      const userId = userData?.uid || 'system';

      const basicWorkflow = {
        workflow_name: t('step11.defaultWorkflow.name'),
        workflow_type: 'clinical',
        workflow_version: '1.0.0',
        description: t('step11.defaultWorkflow.description'),
        purpose: t('step11.defaultWorkflow.purpose'),
        scope: 'hospital-wide',
        triggers: [
          {
            type: 'risk_assessment',
            condition: 'rpn > 100',
            enabled: true
          }
        ],
        nodes: [
          {
            id: 'start',
            type: 'start',
            name: t('step11.defaultWorkflow.nodes.start'),
            position: { x: 100, y: 100 }
          },
          {
            id: 'risk_check',
            type: 'task',
            name: t('step11.defaultWorkflow.nodes.riskAssessment'),
            taskType: 'risk_assessment',
            position: { x: 250, y: 100 }
          },
          {
            id: 'protocol_execution',
            type: 'task',
            name: t('step11.defaultWorkflow.nodes.executeProtocol'),
            taskType: 'protocol_execution',
            position: { x: 400, y: 100 }
          },
          {
            id: 'end',
            type: 'end',
            name: t('step11.defaultWorkflow.nodes.end'),
            position: { x: 550, y: 100 }
          }
        ],
        edges: [
          {
            id: 'e1',
            source: 'start',
            target: 'risk_check'
          },
          {
            id: 'e2',
            source: 'risk_check',
            target: 'protocol_execution'
          },
          {
            id: 'e3',
            source: 'protocol_execution',
            target: 'end'
          }
        ],
        status: 'active'
      };

      await createWorkflowDefinition(basicWorkflow, userId);

      const allWorkflows = await getAllWorkflowDefinitions();
      setWorkflows(allWorkflows || []);

      notify.success(t('step11.notifications.createdSuccess'), t('step11.notifications.createdSuccessTitle'));
      onComplete({ workflows: (allWorkflows || []).length, requirementsMet: true });
    } catch (error) {
      console.error('Error creating workflow:', error);
      notify.error(t('step11.notifications.createFailed'), t('step11.notifications.createFailedTitle'));
    } finally {
      setLoading(false);
    }
  };

  const requirements = {
    workflows: workflows.length >= 1
  };

  return (
    <div className="step-content">
      <div className="step-section">
        <h3>{t('step11.title')}</h3>
        <p>{t('step11.description')}</p>
      </div>

      {!organizationId && (
        <div className="error-message" style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#fff3cd', color: '#856404', borderRadius: '6px' }}>
          {t('step11.completeStep1Warning')}
        </div>
      )}

      <div className="quick-setup-section">
        <div className="quick-setup-option" style={{ cursor: loading || !organizationId ? 'not-allowed' : 'pointer', opacity: loading || !organizationId ? 0.6 : 1 }}>
          <div style={{ textAlign: 'center' }}>
            <h3>{t('step11.quickSetupTitle')}</h3>
            <p>{t('step11.quickSetupDescription')}</p>
            <ul style={{ textAlign: 'left', marginTop: '12px', paddingLeft: '20px' }}>
              <li>{t('step11.quickSetupListWorkflow')}</li>
              <li>{t('step11.quickSetupListRiskAssessment')}</li>
              <li>{t('step11.quickSetupListAutomation')}</li>
            </ul>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleCreateBasicWorkflow}
              disabled={loading || !organizationId}
              style={{ marginTop: '16px' }}
            >
              {loading ? t('step11.creating') : t('step11.createBasicWorkflow')}
            </button>
          </div>
        </div>
      </div>

      <div className="requirements-section" style={{ marginTop: '32px' }}>
        <h3>{t('step11.workflowsTitle')}</h3>
        <div className="requirements-list">
          <div className={`requirement-item ${requirements.workflows ? 'met' : 'pending'}`}>
            {requirements.workflows ? <CheckCircle size={20} color="#4caf50" /> : <AlertCircle size={20} color="#ff9800" />}
            <span>{t('step11.workflowsCount', { count: workflows.length })}</span>
          </div>
        </div>

        {requirements.workflows && (
          <div className="success-message" style={{ marginTop: '20px' }}>
            {t('step11.minimumMet')}
          </div>
        )}
      </div>

      <div className="manual-setup-section" style={{ marginTop: '32px' }}>
        <p style={{ color: '#666', fontStyle: 'italic' }}>
          {t('step11.manualSetupNote')}
        </p>
      </div>
    </div>
  );
};

export default Step11Workflows;
